import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { writeAuditLog } from '../utils/auditLog';
import { Check, Package, Truck, NotePencil, X } from '@phosphor-icons/react';

const toDate = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  return new Date(v);
};
const fmtDate = (v) => {
  const d = toDate(v);
  return d ? d.toISOString().slice(0, 10) : '';
};
const writeLog = async (pharmacyId, user, action, prev, next) => {
  try {
    await addDoc(collection(db, 'pharmacies', pharmacyId, 'activityLogs'), {
      userId: user.uid, displayName: user.displayName || user.email,
      licenceNumber: '', action, previousValue: prev ?? '', newValue: next ?? '',
      timestamp: serverTimestamp(),
    });
  } catch (e) { console.error('activityLog write failed:', e); }
};

function ABtn({ icon, label, color, bg, onClick }) {
  return (
    <button onClick={onClick} title={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '5px 10px', borderRadius: 7, border: 'none',
      background: bg, color: color, fontSize: 11, fontWeight: 600,
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}
    onMouseEnter={e => e.currentTarget.style.opacity = '0.82'}
    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >{icon}<span>{label}</span></button>
  );
}

export default function AlertsPage() {
  const { pharmacy, role, user } = useAuth();
  const [alerts, setAlerts]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [noteModal, setNoteModal] = useState(null);
  const [sevFilter, setSevFilter] = useState('all');
  const canEdit = ['admin', 'pharmacist', 'superadmin', 'technician'].includes(role);

  const load = async () => {
    if (!pharmacy) return;
    setLoading(true);
    const snap = await getDocs(collection(db, 'pharmacies', pharmacy.id, 'patients'));
    const now   = new Date();
    const three = new Date(); three.setDate(three.getDate() + 3);
    const week  = new Date(); week.setDate(week.getDate() + 7);

    const items = [];
    snap.docs.forEach(d => {
      const p = { id: d.id, ...d.data() };
      if (p.status !== 'active' || p.ignored || p.alertDismissed) return;
      const nd = toDate(p.nextDue);
      if (!nd) return;
      if (nd < now && !p.ready)
        items.push({ ...p, alertType: 'overdue',   severity: 'high'   });
      else if (nd <= three && !p.ready)
        items.push({ ...p, alertType: 'due_3days', severity: 'medium' });
      else if (nd <= week && !p.ready)
        items.push({ ...p, alertType: 'due_week',  severity: 'low'    });
    });

    items.sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      if (order[a.severity] !== order[b.severity]) return order[a.severity] - order[b.severity];
      const da = toDate(a.nextDue) || new Date(0);
      const db_ = toDate(b.nextDue) || new Date(0);
      return da - db_;
    });
    setAlerts(items);
    setLoading(false);
  };

  useEffect(() => { load(); }, [pharmacy]);

  const dismiss = async (p) => {
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      alertDismissed: true, updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    load();
  };

  const markReady = async (p) => {
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      ready: true, updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient marked ready', entityType: 'patient',
      entityId: p.id, entityName: p.name,
      previousValue: 'false', newValue: 'true',
      actorRole: role || '', source: 'client',
    });
    load();
  };

  const markPickedUp = async (p) => {
    const today = Timestamp.fromDate(new Date());
    const next  = new Date(); next.setDate(next.getDate() + (p.cycleLength || 28));
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      lastPickup: today, nextDue: Timestamp.fromDate(next),
      ready: false, alertDismissed: false,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    await writeAuditLog(pharmacy.id, user, {
      action: 'Patient marked picked up', entityType: 'patient',
      entityId: p.id, entityName: p.name,
      previousValue: toDate(p.lastPickup)?.toISOString() || '',
      newValue: today.toDate().toISOString(),
      actorRole: role || '', source: 'client',
    });
    load();
  };

  const saveNote = async () => {
    if (!noteModal) return;
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', noteModal.id), {
      note: noteModal.note, updatedAt: serverTimestamp(),
      updatedBy: user.uid, updatedByName: user.email, updatedByRole: role,
    });
    await writeLog(pharmacy.id, user, `Patient note updated: ${noteModal.name}`, '', noteModal.note);
    setNoteModal(null);
    load();
  };

  const SEV = {
    high:   { bg: '#FEE2E2', color: '#DC2626', rowBg: '#FFFBFB', label: 'Overdue'      },
    medium: { bg: '#FEF3C7', color: '#D97706', rowBg: '#FFFDF5', label: 'Prep Now'     },
    low:    { bg: '#DBEAFE', color: '#1768C4', rowBg: '#FAFCFF', label: 'Due This Week' },
  };

  const counts = {
    high:   alerts.filter(a => a.severity === 'high').length,
    medium: alerts.filter(a => a.severity === 'medium').length,
    low:    alerts.filter(a => a.severity === 'low').length,
  };

  const displayed = sevFilter === 'all' ? alerts : alerts.filter(a => a.severity === sevFilter);

  const headers = ['Severity', 'Patient', 'Last Pickup', 'Next Due', 'Urgency', 'Flags', 'Actions'];

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Alerts</h1>
          <p style={{ margin: 0, fontSize: 13, color: '#94A3B8' }}>
            {loading ? 'Loading…' : `${alerts.length} patient${alerts.length !== 1 ? 's' : ''} needing attention`}
          </p>
        </div>
        {/* Filter pills */}
        {!loading && alerts.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {[['all', 'All', '#0F172A', '#F1F5F9'],
              ['high', `${counts.high} Overdue`, SEV.high.color, SEV.high.bg],
              ['medium', `${counts.medium} Prep Now`, SEV.medium.color, SEV.medium.bg],
              ['low', `${counts.low} Due This Week`, SEV.low.color, SEV.low.bg],
            ].map(([key, label, color, bg]) => counts[key] > 0 || key === 'all' ? (
              <button key={key} onClick={() => setSevFilter(key)} style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: sevFilter === key ? color : bg,
                color: sevFilter === key ? '#fff' : color,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              }}>{label}</button>
            ) : null)}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
      ) : alerts.length === 0 ? (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0',
          padding: 56, textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: 12, color: '#16A34A' }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>All caught up!</div>
          <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 4 }}>No patients need attention right now.</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {headers.map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((a, i) => {
                const sc       = SEV[a.severity];
                const nd       = toDate(a.nextDue);
                const lp       = toDate(a.lastPickup);
                const now      = new Date();
                const diffDays = nd ? Math.round((now - nd) / 86400000) : null;
                const lpDays   = lp ? Math.round((now - lp) / 86400000) : null;

                return (
                  <tr key={`${a.id}-${i}`} style={{
                    borderBottom: '1px solid #F1F5F9',
                    background: sc.rowBg,
                  }}>

                    {/* Severity */}
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11,
                        fontWeight: 700, background: sc.bg, color: sc.color, whiteSpace: 'nowrap',
                      }}>{sc.label}</span>
                    </td>

                    {/* Patient name */}
                    <td style={{ padding: '11px 14px', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>
                      {a.name}
                      {a.note && (
                        <div style={{ fontSize: 11, color: '#D97706', marginTop: 2, fontWeight: 400 }}>
                          📝 {a.note.length > 40 ? a.note.slice(0, 40) + '…' : a.note}
                        </div>
                      )}
                    </td>

                    {/* Last Pickup */}
                    <td style={{ padding: '11px 14px', color: '#64748B', whiteSpace: 'nowrap' }}>
                      {lp ? (
                        <div>
                          <div style={{ fontWeight: 500 }}>{fmtDate(a.lastPickup)}</div>
                          {lpDays !== null && (
                            <div style={{ fontSize: 11, color: lpDays > 60 ? '#DC2626' : '#94A3B8', marginTop: 1 }}>
                              {lpDays}d ago
                            </div>
                          )}
                        </div>
                      ) : <span style={{ color: '#DC2626', fontWeight: 600 }}>Never</span>}
                    </td>

                    {/* Next Due */}
                    <td style={{ padding: '11px 14px', color: '#64748B', whiteSpace: 'nowrap' }}>
                      {nd ? fmtDate(a.nextDue) : '—'}
                    </td>

                    {/* Urgency */}
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      {diffDays !== null && (
                        <span style={{
                          fontWeight: 700,
                          color: diffDays > 7 ? '#991B1B' : diffDays > 0 ? '#DC2626' : '#D97706',
                          fontSize: diffDays > 7 ? 13 : 12,
                        }}>
                          {diffDays > 0 ? `${diffDays}d overdue` : diffDays === 0 ? 'Due today' : `in ${Math.abs(diffDays)}d`}
                        </span>
                      )}
                    </td>

                    {/* Flags */}
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {a.deliveryPatient && (
                          <span style={{
                            padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                            background: '#DBEAFE', color: '#1768C4', whiteSpace: 'nowrap',
                          }}>🚚 Delivery</span>
                        )}
                        {lpDays !== null && lpDays > 90 && (
                          <span style={{
                            padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                            background: '#FEE2E2', color: '#DC2626', whiteSpace: 'nowrap',
                          }}>⚠ Long Absence</span>
                        )}
                        {!a.lastPickup && (
                          <span style={{
                            padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                            background: '#FEE2E2', color: '#DC2626', whiteSpace: 'nowrap',
                          }}>No Pickup Record</span>
                        )}
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '8px 14px' }}>
                      {canEdit ? (
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {!a.ready && a.severity === 'high' && (
                            <ABtn icon={<Check size={12} weight="bold" />} label="Ready"
                              color="#16A34A" bg="#DCFCE7" onClick={() => markReady(a)} />
                          )}
                          <ABtn icon={<Package size={12} weight="bold" />} label="Picked Up"
                            color="#1768C4" bg="#DBEAFE" onClick={() => markPickedUp(a)} />
                          <ABtn icon={<NotePencil size={12} weight="bold" />} label="Note"
                            color={a.note ? '#D97706' : '#64748B'} bg={a.note ? '#FEF3C7' : '#F1F5F9'}
                            onClick={() => setNoteModal({ id: a.id, name: a.name, note: a.note || '' })} />
                          <ABtn icon={<X size={12} weight="bold" />} label="Dismiss"
                            color="#64748B" bg="#F1F5F9" onClick={() => dismiss(a)} />
                        </div>
                      ) : (
                        <ABtn icon={<X size={12} weight="bold" />} label="Dismiss"
                          color="#64748B" bg="#F1F5F9" onClick={() => dismiss(a)} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: 16, fontSize: 11, color: '#CBD5E1' }}>
        Dismiss hides this alert until the next cycle. To permanently exclude a patient, use Ignore in the Patients page.
      </p>

      {/* Note Modal */}
      {noteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Patient Note</h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748B' }}>{noteModal.name}</p>
            <textarea
              value={noteModal.note}
              onChange={e => setNoteModal({ ...noteModal, note: e.target.value })}
              placeholder="Add a note about this patient..."
              style={{ width: '100%', minHeight: 100, padding: '10px 12px', borderRadius: 8,
                border: '1px solid #E2E8F0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setNoteModal(null)} style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #E2E8F0',
                background: '#F8FAFC', color: '#64748B', fontSize: 13, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={saveNote} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#0F172A', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600,
              }}>Save Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
